import { ErrorCode } from '@afterpaytouch/portal-api'
import { TerminalErrorCode, isTerminalErrorCode } from '../../model/checkout'
import { JourneyOutcome } from './types'

export type JourneySignalType = 'friction' | 'frustration'

export interface TerminalErrorJourneyMapping {
  outcome: JourneyOutcome
  signalType: JourneySignalType
}

/**
 * FAILED is reserved for unexpected system failures, so the journey SLI only dips on
 * real outages (per the UJ validation guide). Expected business-logic terminals —
 * declines, limits, blocks, attempt lockouts — end the journey as USER_CANCELED with a
 * friction signal: the system worked correctly, the user's goal was unmet through no
 * fault of ours, and friction never counts toward BAD in the SLI. This also keeps
 * SUCCEEDED meaning strictly "payment confirmed".
 */
const systemFailure: TerminalErrorJourneyMapping = { outcome: JourneyOutcome.FAILED, signalType: 'frustration' }
const businessDecline: TerminalErrorJourneyMapping = { outcome: JourneyOutcome.USER_CANCELED, signalType: 'friction' }

/**
 * The reviewed mapping table required by CASHDAP-3405 (feeds CASHDAP-3406). Exhaustive
 * over TerminalErrorCode — adding a code without classifying it here is a compile error.
 */
export const TERMINAL_ERROR_JOURNEY_MAPPING: Record<TerminalErrorCode, TerminalErrorJourneyMapping> = {
  // Unexpected system failures — our fault, dips the SLI
  [TerminalErrorCode.Default]: systemFailure,
  [TerminalErrorCode.UnableToUpdateCheckout]: systemFailure,
  [TerminalErrorCode.LimitInternalError]: systemFailure,
  [TerminalErrorCode.ErrorPCL]: systemFailure,
  [TerminalErrorCode.IdentityVerificationTimeout]: systemFailure,

  // Account state blocks — expected terminals
  [TerminalErrorCode.AccountClosed]: businessDecline,
  [TerminalErrorCode.AccountDisabled]: businessDecline,
  [TerminalErrorCode.AccountFrozen]: businessDecline,
  [TerminalErrorCode.AccountPendingDeletion]: businessDecline,
  [TerminalErrorCode.ChargebackPending]: businessDecline,
  [TerminalErrorCode.OverduePayments]: businessDecline,
  [TerminalErrorCode.OverduePaymentsCheckoutPaymentEligible]: businessDecline,
  [TerminalErrorCode.ExcessiveLateFeeAmount]: businessDecline,
  [TerminalErrorCode.ExcessiveLateFeeNumber]: businessDecline,
  [TerminalErrorCode.KYCRefreshPending]: businessDecline,
  [TerminalErrorCode.ShadowAccountIneligible]: businessDecline,

  // Risk / lending / compliance declines — expected terminals
  [TerminalErrorCode.RiskDeclined]: businessDecline,
  [TerminalErrorCode.RiskDeclinedPCL]: businessDecline,
  [TerminalErrorCode.AuthenticationDeclined]: businessDecline,
  [TerminalErrorCode.PasswordResetDeclined]: businessDecline,
  [TerminalErrorCode.PasswordResetNotAllowed]: businessDecline,
  [TerminalErrorCode.IdentityDeclined]: businessDecline,
  [TerminalErrorCode.IdentityVerificationFailedManual]: businessDecline,
  [TerminalErrorCode.IdentityVerificationDeclinedPCL]: businessDecline,
  [TerminalErrorCode.PaywatchCheckFailed]: businessDecline,
  [TerminalErrorCode.ExtendedFraudAlert]: businessDecline,
  [TerminalErrorCode.CreditFreezeDeclinedPCL]: businessDecline,
  [TerminalErrorCode.ComplianceDeclineBureauNoHit]: businessDecline,
  [TerminalErrorCode.ComplianceDeclineBureau]: businessDecline,
  [TerminalErrorCode.CreditCheckFailure]: businessDecline,
  [TerminalErrorCode.UnsuitabilityDecline]: businessDecline,
  [TerminalErrorCode.AffordabilityDecline]: businessDecline,
  [TerminalErrorCode.HighRiskNationality]: businessDecline,
  [TerminalErrorCode.DuplicateIdentity]: businessDecline,
  [TerminalErrorCode.BlockSignUpFromFRITES]: businessDecline,

  // Order / amount limits — expected terminals
  [TerminalErrorCode.AmountTooHigh]: businessDecline,
  [TerminalErrorCode.AmountTooHighDeclinePCL]: businessDecline,
  [TerminalErrorCode.OrderAmountTooHigh]: businessDecline,
  [TerminalErrorCode.OrderAmountExceedsSpendCap]: businessDecline,
  [TerminalErrorCode.MaxActiveOrdersReached]: businessDecline,
  [TerminalErrorCode.MaxActiveOrdersReachedDetails]: businessDecline,
  [TerminalErrorCode.MaxActiveOrdersReachedDeclinePCL]: businessDecline,
  [TerminalErrorCode.MerchantOrderAmountLimitExceeded]: businessDecline,
  [TerminalErrorCode.MerchantLimitZero]: businessDecline,
  [TerminalErrorCode.MinimumNotMet]: businessDecline,
  [TerminalErrorCode.InsufficientFunds]: businessDecline,
  [TerminalErrorCode.InsufficientFundsDecline]: businessDecline,

  // Order / merchant ineligibility — expected terminals
  [TerminalErrorCode.InvalidPreorder]: businessDecline,
  [TerminalErrorCode.UnsupportedOrder]: businessDecline,
  [TerminalErrorCode.UnsupportedMerchantTradingCountry]: businessDecline,
  [TerminalErrorCode.InvalidAmount]: businessDecline,
  [TerminalErrorCode.InvalidAddress]: businessDecline,
  [TerminalErrorCode.InvalidPickupAddress]: businessDecline,
  [TerminalErrorCode.IneligibleState]: businessDecline,
  [TerminalErrorCode.StateRestrictionsPCL]: businessDecline,
  [TerminalErrorCode.GiftCardIneligible]: businessDecline,
  [TerminalErrorCode.DuplicatePaymentCardError]: businessDecline,

  // Attempt lockouts — expected terminals (user exceeded a safety limit)
  [TerminalErrorCode.MaxAttempts]: businessDecline,
  [TerminalErrorCode.MaxAttemptsForType]: businessDecline,
  [TerminalErrorCode.MaxDobVerificationAttempts]: businessDecline,
  [TerminalErrorCode.BiometricCheckMaxRetriesReached]: businessDecline,
  [TerminalErrorCode.IdentityVerificationMaxAttemptsAFF]: businessDecline,
  [TerminalErrorCode.ExceededMaxAttemptsEnteringDetails]: businessDecline,
}

export interface TerminalErrorJourneyOutcome extends TerminalErrorJourneyMapping {
  signal: string
}

/**
 * Raw portal error codes that are system faults even though the checkoutErrors display
 * mapping funnels them into a business-decline TerminalErrorCode. IdentityCheckGatewayError
 * shares TerminalErrorCode.IdentityDeclined with genuine declines and lockouts
 * (IdentityCheckRejected, MaxAttemptsReached, VerifiedMobileMissing — model/checkout.ts),
 * because they all render the same /oh-no screen — but an IDV gateway outage is our fault,
 * and classifying it USER_CANCELED would hide exactly the SLI dip this mapping exists to
 * surface. The in-flow IDV modal already treats this code as a retryable service failure
 * (IdentityServiceFailureError). Checked against the raw errorCode before everything else;
 * the raw code becomes the signal so outages are distinguishable from declines on dashboards.
 */
const SYSTEM_FAULT_ERROR_CODES: string[] = [ErrorCode.IdentityCheckGatewayError]

/**
 * Two /oh-no paths carry a CheckoutError.error that is never a stable TerminalErrorCode,
 * so the enum lookup can't classify them and they would dip the SLI through the FAILED
 * fallback despite being expected business declines (CASHDAP-3422):
 * - risk_decline_unclassified — the reducer surfaces the camelCased Rosetta decline
 *   reason as `error`, an open-ended string.
 * - duplicate_identity — has no checkoutErrors entry, so the IDV routing degrades it to
 *   TerminalErrorCode.Default.
 * The raw backend errorCode still identifies both, so classify on it first.
 */
const ERROR_CODE_RECLASSIFICATIONS: Partial<Record<string, TerminalErrorCode>> = {
  [ErrorCode.RiskDeclineUnclassified]: TerminalErrorCode.RiskDeclined,
  [ErrorCode.DuplicateIdentity]: TerminalErrorCode.DuplicateIdentity,
}

/**
 * Unmapped codes deliberately fall back to FAILED so they surface as SLI dips and get
 * classified, instead of silently inflating GOOD outcomes. Raw-errorCode checks run
 * before the enum lookup — system faults first (key sets are disjoint today, but a
 * fault must never be downgraded to a decline), then decline reclassifications.
 */
export const mapTerminalErrorToJourney = (error: unknown, errorCode?: unknown): TerminalErrorJourneyOutcome => {
  if (typeof errorCode === 'string' && SYSTEM_FAULT_ERROR_CODES.includes(errorCode)) {
    return { ...systemFailure, signal: errorCode }
  }
  const reclassified = typeof errorCode === 'string' ? ERROR_CODE_RECLASSIFICATIONS[errorCode] : undefined
  if (reclassified !== undefined) {
    // Keep the specific reason (the Rosetta decline reason) as the signal when the error
    // field carries one; a degraded enum value (Default) says less than the reclassified code
    const signal = typeof error === 'string' && error.length > 0 && !isTerminalErrorCode(error) ? error : reclassified
    return { ...TERMINAL_ERROR_JOURNEY_MAPPING[reclassified], signal }
  }
  if (isTerminalErrorCode(error)) {
    return { ...TERMINAL_ERROR_JOURNEY_MAPPING[error], signal: error }
  }
  return { ...systemFailure, signal: typeof error === 'string' && error.length > 0 ? error : TerminalErrorCode.Default }
}
